import * as Sentry from "@sentry/browser"
import {capitalizeFirst} from 'js/studios/shared/utils/strings'

class ApiError extends Error {
  constructor(message, httpStatus, origBody) {
    super(message)
    this.httpStatus = httpStatus
    this.origBody = origBody
  }
}

/* Convert various types of objects into an error message for display. Primarily expects either an
 * error instance (with a .message), or a string */
export function apiErrorMessage(err) {
  if (!err) {
    return null
  } else if (err.message) {
    return err.message
  } else if (typeof err === 'string') {
    return err
  } else {
    return toString(err)
  }
}

// given a response object, extract the body json & primary error message
async function extractJsonRespError(resp) {
  if (resp.headers.get("Content-Type") !== "application/json") {
    return [null, null]
  }

  const body = await resp.text()
  if (body) {
    const bodyJSON = JSON.parse(body)
    if (typeof bodyJSON === 'object' && bodyJSON !== null) {
      const err = bodyJSON.error || bodyJSON.errors
      if (typeof err === "string") {
        return [capitalizeFirst(err), bodyJSON]
      } else if (Array.isArray(err) && typeof err[0] === 'string') {
        return [capitalizeFirst(err[0]), bodyJSON]
      } else if (typeof err === "object") {
        // some endpoints (e.g. registration) return more structured errors by fields. The only ones
        // I've seen are like {field: [errMsg]}, but we'll handle {field: errMsg} as well.
        let errMsg = ""
        for (const field in err) {
          if (typeof err[field] === "string") {
            errMsg += capitalizeFirst(err[field])
          } else if (Array.isArray(err[field]) && typeof err[field][0] === "string" ) {
            errMsg += capitalizeFirst(err[field][0])
          }
          if (errMsg.length && !errMsg.endsWith(".")) {
            errMsg += "."
          }
        }
        return [errMsg || "Unknown error occurred", bodyJSON]
      } else { // fallback so we don't return undefined
        Sentry.withScope(scope => {
          scope.setLevel("warning")
          scope.setExtras({errJSON: bodyJSON})
          Sentry.captureMessage("Unexpected error format from API response, extractJsonRespError might need improvements")
        })
        return ["Unknown error occurred", bodyJSON]
      }
    }
  }

  return [null, null] // last-ditch fallback if resp claimed to be JSON but had no body
}

// the attachments api is weird and old and does not return an `error` or `errors` key, it returns
// {field: [error]} (top-level).
async function extractAttachmentJsonRespError(bodyText) {
  const bodyJSON = JSON.parse(bodyText)
  const errs = []

  for (const field of Object.keys(bodyJSON)) {
    errs.push(bodyJSON[field][0])
  }

  return [errs.join(" "), bodyJSON]
}

/* A client implemented on top of `fetch` and Promises for internal API calls to begin modernizing
 * our front-end code.
 *
 * There are methods on the client for common HTTP methods. Each follows the basic interface of
 * `api.method(uri, data, opts)`. `opts` is passed through to `fetch`, merged with other `fetch`
 * options the api client constructs internally. It's mostly available so clients can pass a
 * `signal` from an `AbortController` for cases where we need to abort requests.
 * */
class ApiClient {
  get csrfToken() {
    return document.querySelector('meta[name=csrf-token]').content
  }

  fetch = async (uri, opts) => {
    return new Promise((resolve, reject) => {
      window.fetch(uri, opts)
        .then(resp => resolve(resp))
        .catch(err => {
          if (err.name === "AbortError") {
            // if the request was given an AbortController.signal and that signal aborted the
            // request, we neither resolve nor reject the promise so clients don't need to handle
            // the abort.
            return
          } else if (err instanceof TypeError && ["Failed to fetch", "Load failed"].includes(err.message)) {
            reject(new ApiError("Failed to reach our servers. Please try again, check your connection if this continues."))
          } else {
            reject(err)
          }
        })
    })
  }

  // params can be a URLSearchParams instance, or any object usable as the argument to the
  // constructor of URLSearchParams. That includes a POJO like {paramName: val, anotherParam: val}.
  // Note that values with multiple values, e.g. `{x: [1,2]}`, are not handled correctly when passed
  // as directly to the URLSearchParams constructor, so if you have multi-value parameters you
  // should construct the URLSearchParams instance yourself
  get = async (uri, params=null, opts) => {
    if (params) {
      if (!(params instanceof URLSearchParams)) {
        params = new URLSearchParams(params)
      }
      uri = `${uri}${uri.endsWith("?") ? "" : "?"}${params.toString()}`
    }

    return this.processResponse(await this.fetch(uri, {
      ...(opts || {}),
      headers: {
        "Accept": "application/json",
      },
    }))
  }

  // data should be a plain object. It will be serialized to JSON and used as the POST body.
  post = async (uri, data=null, opts) => {
    return this.processResponse(await this.fetch(uri, {
      ...(opts || {}),
      method: "POST",
      headers: {
        "X-CSRFToken": this.csrfToken,
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      body: data ? JSON.stringify(data) : null,
    }))
  }

  patch = async (uri, data=null, opts) => {
    return this.processResponse(await this.fetch(uri, {
      ...(opts || {}),
      method: "PATCH",
      headers: {
        "X-CSRFToken": this.csrfToken,
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      body: data ? JSON.stringify(data) : null,
    }))
  }

  // attachments get their own upload method because they track progress, which is not currently
  // standard supported behavior for fetch, so this still uses xhr. progressCallback, if given,
  // receives progress as a float percentage (e.g. 0.5 means 50% done uloading).
  uploadAttachment = (file, progressCallback) => {
    return new Promise((resolve, reject) => {
      const data = new FormData()
      data.append('data', file)
      // tmp allows not-logged-in users to upload. Not sure we still need this. Also not sure we ever
      // unmark uploaded attachments as tmp?
      data.append('tmp', true)

      const xhr = new XMLHttpRequest()
      xhr.upload.addEventListener("progress", ev => {
        if (progressCallback && ev.lengthComputable) {
          progressCallback(ev.loaded / ev.total)
        }
      })
      xhr.addEventListener("loadend", () => {
        if (xhr.readyState === 4 && xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText))
        } else if (xhr.readyState === 4) {  // must be an error response (400, 500, etc.)
          extractAttachmentJsonRespError(xhr.responseText)
            .then(([errMsg, bodyJSON]) => reject(new ApiError(errMsg, xhr.status, bodyJSON)))
            .catch(() => reject(new ApiError("An unknown server error occurred.")))
        } else {  // http connection failed or something
          reject(new ApiError("An unknown network error occurred."))
        }
      })
      xhr.open("POST", window.cm.urls['attachments-api'], true)
      // xhr.setRequestHeader("Content-Type", "multipart/form-data")
      xhr.setRequestHeader("Accept", "application/json")
      xhr.setRequestHeader("X-CSRFToken", this.csrfToken)
      xhr.send(data)
    })
  }

  // make a DELETE request, optionally with a body. Body serialization is handled same as POST.
  delete = async (uri, body=null) => {
    return this.processResponse(await this.fetch(uri, {
      method: "DELETE",
      headers: {
        "X-CSRFToken": this.csrfToken,
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      body: body ? JSON.stringify(body) : null,
    }))
  }

  processResponse = async (resp) => {
    if (resp.status >= 200 && resp.status < 300) {
      if (resp.status !== 204 && resp.headers.get("Content-Type") !== "application/json") {
        throw new ApiError("Bad response", resp.status)
      }

      const body = await resp.text()
      if (body) {
        return JSON.parse(body)
      }
      return null
    } else if (resp.status === 400) {
      if (resp.headers.get("Content-Type") !== "application/json") {
        throw new ApiError("Bad response", resp.status)
      }

      const [errMsg, bodyJSON] = await extractJsonRespError(resp)
      if (errMsg) { throw new ApiError(errMsg, resp.status, bodyJSON) }
      throw new ApiError("Unknown error", resp.status)
    } else if (resp.status === 403) {
      let msg = "Forbidden. You may need to login again.",
          bodyJSON = null

      if (resp.headers.get("Content-Type") === "application/json") {
        const [msg2, bodyJSON2] = await extractJsonRespError(resp)
        if (msg2 && bodyJSON2) {
          msg = msg2
          bodyJSON = bodyJSON2
        }
      } else {
        const body = await resp.text()
        if (body.toLowerCase().includes('csrf')) {
          msg = "Please refresh the page and try again. If the problem persists, contact support."
        }
      }

      throw new ApiError(msg, resp.status, bodyJSON)
    } else if (resp.status === 404) {
      if (resp.headers.get("Content-Type") !== "application/json") {
        throw new ApiError("Not found. You may need to refresh the page to see the latest information.", resp.status)
      }

      const [errMsg, bodyJSON] = await extractJsonRespError(resp)
      if (errMsg) { throw new ApiError(errMsg, resp.status, bodyJSON) }
      throw new ApiError("Not found. You may need to refresh the page to see the latest information.", resp.status)
    } else if (resp.status === 413) {
      throw new ApiError("File is too large", resp.status)
    } else if (resp.status >= 400 && resp.status < 500) {
      throw new ApiError("Bad request", resp.status)
    } else if (resp.status >= 500) {
      throw new ApiError("An unknown server error ocurred", resp.status)
    } else {
      throw new ApiError("An unknown error ocurred", resp.status)
    }
  }
}

export const api = new ApiClient()
